import React from 'react';
import ReactTable from 'react-table';
import styled from 'styled-components';
import Label from './Label';
import TimeUtils, { TimeFormat } from '../utils/TimeUtils';
/**
 * Column definitions
 */
const COLUMNS = [
    {
        Header: '#',
        accessor: 'order',
        minWidth: 15,
        maxWidth: 15
    },
    {
        Header: 'Source',
        accessor: 'source',
        minWidth: 90,
        maxWidth: 90
    },
    {
        Header: 'Details',
        accessor: 'details',
        Cell: props => (React.createElement(DetailsTableData, { isError: props.original.error },
            props.original.details,
            React.createElement(ErrorLabel, { isVisible: props.original.error, text: props.original.error })))
    },
    {
        Header: 'Time',
        accessor: 'latency_millis',
        minWidth: 40,
        maxWidth: 40,
        Cell: props => `${TimeUtils.convertTimeFromTo(props.original.latency_millis, TimeFormat.Milliseconds, TimeFormat.Seconds)}`
    }
];
export default class DebugInfo extends React.Component {
    /**
     * Renders debug controls
     */
    renderControls(data) {
        let totalTime = 0;
        let totalErrors = 0;
        // Iterate through each table row and accumulate counters
        data.forEach(row => {
            totalTime += row.latency_millis;
            totalErrors += (row.error ? 1 : 0);
        });
        // Convert total time to seconds
        totalTime = TimeUtils.convertTimeFromTo(totalTime, TimeFormat.Milliseconds, TimeFormat.Seconds);
        return (React.createElement(DebugInfoControlsWrapper, null,
            React.createElement(DebugInfoControls, null,
                React.createElement(TotalTimeLabel, { text: 'Total Time:' }),
                React.createElement(TimeLabel, { text: `${totalTime} seconds` }),
                React.createElement(TotalErrorsLabel, { text: totalErrors ? // Determine how many errors to render for label
                        `${totalErrors} Error${totalErrors > 1 ? 's' : ''}` : // 1 or more errors
                        '' // No errors
                 }))));
    }
    /**
     * Renders debug controls and table
     */
    render() {
        const { data, className, } = this.props;
        if (!Array.isArray(data)) {
            return (React.createElement(DebugInfoWrapper, { className: className },
                React.createElement(EmptyMessage, null, "No debug info to display. Access a page that supports debug info to see it here.")));
        }
        return (React.createElement(DebugInfoWrapper, { className: className },
            this.renderControls(data),
            React.createElement(DebugInfoTable, { TableComponent: TableComponent, TheadComponent: TheadComponent, TbodyComponent: TbodyComponent, TrGroupComponent: TrGroupComponent, TrComponent: TrComponent, ThComponent: ThComponent, TdComponent: TdComponent, TfootComponent: TfootComponent, LoadingComponent: () => React.createElement("div", null), columns: COLUMNS, data: data, loading: false, minRows: 0, defaultPageSize: 1000, showPagination: false })));
    }
}
const EmptyMessage = styled.p `
  font-family: ${props => props.theme.fonts.regular};

  margin: 0;
  padding: 16px;

  background-color: ${props => props.theme.colors.silverLighter};
  color: ${props => props.theme.colors.tertiaryText};
  line-height: 30px;

  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;
const DebugInfoWrapper = styled.div `
  display: block;
`;
const DebugInfoControlsWrapper = styled.div `
  display: flex;
  flex-direction: row;

  padding: 8px;

  background-color: ${props => props.theme.colors.silverLighter};
`;
const DebugInfoControls = styled.div `
  display: contents;
`;
const TotalTimeLabel = styled(Label) `
  margin-left: 8px;

  color: ${props => props.theme.colors.tertiaryText};
  line-height: 30px;
`;
const TimeLabel = styled(Label) `
  margin-left: 4px;

  color: ${props => props.theme.colors.tertiaryText};
  line-height: 30px;
`;
const TotalErrorsLabel = styled(Label) `
  margin-left: 16px;

  color: ${props => props.theme.colors.textOrange};
  line-height: 30px;
`;
const ErrorLabel = styled(({ isVisible, ...rest }) => React.createElement(Label, { ...rest })) `
  display: ${props => props.isVisible ? 'block' : 'none'};

  padding-top: 10px;

  font-family: monospace;
  color: ${props => props.theme.colors.textOrange};
`;
const DetailsTableData = styled("span") `
  color: ${props => props.theme.colors.blackLight};
  color: ${props => props.isError && props.theme.colors.textOrange};
`;
const DebugInfoTable = styled((props) => React.createElement(ReactTable, { ...props })) `
  transition: max-height 0.3s ease-in-out;

  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;
const TableComponent = styled.table `
  width: 100%;

  margin: 0;
  padding: 0;
  border-collapse: collapse;

  font-family: ${props => props.theme.fonts.regular};
  text-align: left;
  font-size: 13px;
  font-weight: 300;
  word-break: break-all;
`;
const TheadComponent = styled.thead `
  font-family: ${props => props.theme.fonts.demi};

  background-color: ${props => props.theme.colors.silverLighter};
`;
const TbodyComponent = styled.tbody `
 background-color: ${props => props.theme.colors.white};

  tr {
    box-shadow: inset 0 -1px 0 0 rgba(150,159,170,0.20);
  }
`;
const TrGroupComponent = ({ children }) => (React.createElement(React.Fragment, null, children));
const TrComponent = styled.tr `
  :hover {
    background: rgba(216,238,252,0.40);
  }
`;
const ThComponent = ({ toggleSort, className, children, ...rest }) => (React.createElement(ThComponentContent, { onClick: e => toggleSort && toggleSort(e), role: "columnheader", tabIndex: -1, libraryClasses: className, ...rest }, children));
const ThComponentContent = styled("th") `
  padding: 9px 6px;

  outline: none;

  border-right: 1px solid rgba(150,159,170,0.20);
  :last-child {
    border-right: none;
  }

  box-shadow: none;
  box-shadow: ${props => props.libraryClasses.includes('sort-asc') && `inset 0 2px 0 0 ${props.theme.colors.tertiaryText}`};
  box-shadow: ${props => props.libraryClasses.includes('sort-desc') && `inset 0 -2px 0 0 ${props.theme.colors.tertiaryText}`};
`;
const TdComponent = styled.td `
  width: auto !important;

  margin: 0;
  padding: 5px;

  border-right: 1px solid rgba(150,159,170,0.20);

  :first-child {
    text-align: center;
  }

  :nth-child(3) {
    padding: 10px;

    font-family: monospace;
  }

  :last-child {
    border-right: none;
  }
`;
const TfootComponent = styled.tfoot `
  font-family: ${props => props.theme.fonts.demi};
  background-color: ${props => props.theme.colors.silverLight};
`;
//# sourceMappingURL=DebugInfo.js.map